import _ from 'lodash';
import type { ZodType } from 'zod';

import type { ModuleKey } from './defaults';
import { defaultModules } from './defaults';
import type { Module, ModuleConfig } from './types';

/**
 * Check if a module is enabled by traversing a dot-notation path.
 * E.g. `isModuleEnabled(modules, 'risk.subModules.appetite')` checks
 * `modules.risk.subModules.appetite.enabled`.
 *
 * Also verifies all ancestor modules are enabled. A child module is
 * considered disabled if any of its parents are disabled.
 */
export const isModuleEnabled = (
  modules: ModuleConfig,
  id: ModuleKey
): boolean => {
  // Split 'risk.subModules.impact' into module key segments ['risk', 'impact'],
  // filtering out the structural 'subModules' parts.
  const keys = id.split('.').filter((s) => s !== 'subModules');

  let current: Record<string, Module> = modules;
  for (const key of keys) {
    const mod: Module | undefined = current[key];
    if (!mod?.enabled) {
      return false;
    }
    current = mod.subModules ?? {};
  }

  return true;
};

/**
 * Recursively walk through a module config and collect **flattened** dot-notation
 * paths of all enabled modules (e.g. `'risk'`, `'risk.impact'`).
 *
 * These paths omit the `.subModules.` segments used by `ModuleKey` and are NOT
 * compatible with `isModuleEnabled`. They are designed for scope matching in the
 * external API where `ResourceScope.module` uses the flattened format.
 *
 * Sub-modules are only walked if their parent is enabled.
 */
export const collectEnabledModulePaths = (
  config: ModuleConfig
): Set<string> => {
  const paths = new Set<string>();

  const walk = (module: Module, path: string) => {
    if (module.enabled) {
      paths.add(path);

      if (module.subModules) {
        for (const [childKey, childModule] of Object.entries(
          module.subModules
        )) {
          walk(childModule, `${path}.${childKey}`);
        }
      }
    }
  };

  for (const [rootKey, module] of Object.entries(config)) {
    walk(module, rootKey);
  }

  return paths;
};

/**
 * Get a typed config value from a module, safely parsed with a Zod schema.
 * Returns the parsed value on success, or `defaultValue` if the key is missing
 * or the value doesn't match the schema.
 *
 * E.g. `getModuleConfigValue(module, 'RiskScoringModel', z.nativeEnum(Risk_Scoring_Model_Enum), Risk_Scoring_Model_Enum.Default)`
 */
export const getModuleConfigValue = <T>(
  module: Module | undefined,
  params: { key: string; schema: ZodType<T>; defaultValue: T }
): T => {
  const value = module?.config?.[params.key];
  const result = params.schema.safeParse(value);

  return result.success ? result.data : params.defaultValue;
};

/**
 * Merge org-specific module settings on top of defaults.
 * Returns a full module config with defaults filled in for any missing keys.
 */
export const mergeModulesWithDefaults = (
  orgModules: Partial<ModuleConfig>
): ModuleConfig => {
  return _.merge(
    {},
    _.cloneDeep(defaultModules),
    _.cloneDeep(orgModules)
  ) as ModuleConfig; // _.merge returns a generic object; we know the shape matches ModuleConfig
};
